import type { KnowledgeBase, KnowledgeBaseView } from '@ralph/contracts'
import clsx from 'clsx'
import { CircleCheck, Plus, Search, TriangleAlert, X } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { NewBaseDialog } from '@/components/knowledge/BaseDialogs.tsx'
import { BaseRow } from '@/components/knowledge/BaseList.tsx'
import { BasePanel } from '@/components/knowledge/BasePanel.tsx'
import { defaultView, legacyTarget, matchesQuery, parseView } from '@/components/knowledge/knowledge-base.ts'
import { useKnowledgeViewer } from '@/components/knowledge/knowledge-viewer.ts'
import { Notice } from '@/components/ui/admin-chrome.tsx'
import { Button, EmptyState, Input, Spinner } from '@/components/ui/primitives.tsx'
import { useKnowledgeBases } from '@/hooks/use-knowledge-bases.ts'
import { useI18n } from '@/i18n/index.ts'

/**
 * Knowledge (PRD-26 *The interface* → Knowledge): every knowledge base this person maintains or
 * can use, and one of them opened beside the list.
 *
 * ── The list ──
 *
 * *You maintain · You can use · All* — the last for administrators only. The two are different
 * relationships and the page never blurs them: a base you maintain is yours to fill, share and
 * move; a base you can use was shared with you, and the row says by which group. Each row says
 * why the reader has it, with the group's avatar wherever a group is the reason (GRP-102), how
 * much of it answers, and the folders that feed it. With no view in the address, the list opens
 * on what you maintain — or, when you maintain nothing, on what you can use.
 *
 * ── The selection lives in the address ──
 *
 * `/knowledge/:baseId`, like a group's page: a notification, a bookmark or the back button lands
 * on the same base, and a row is a link that can be opened in a new tab. Below `lg` the base is a
 * full-width push with a way back. The addresses from before PRD-26 — `?source=<id>`, which the
 * command palette and a connected folder's sign-in still send — are carried onto the base's own.
 *
 * ── Making one ──
 *
 * Anybody may make a knowledge base, for themselves or for a group they manage; an administrator
 * for any group or person. The dialog states the chosen owner's limits before anything is sent
 * (D-41), and a new base is usable only by its maintainers until they choose otherwise.
 */

type DeletedState = { deleted?: string } | null

export const KnowledgePage = () => {
  // Read inside the body, never captured at module scope: a capture taken at import freezes the
  // language and renders the previous one after a switch, with nothing failing (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const { baseId } = useParams<{ baseId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const viewer = useKnowledgeViewer()
  const searchId = useId()
  const listLabelId = useId()

  const [q, setQ] = useState('')
  const [creating, setCreating] = useState(false)
  const [dismissed, setDismissed] = useState<string | null>(null)
  // A connected folder's sign-in that failed before the platform knew which base it was for comes
  // back here, with no base to resume on; read once, then taken out of the address.
  const [signInFailed] = useState(() => (searchParams.get('source') ? null : searchParams.get('connectFailed')))

  const legacy = legacyTarget(searchParams)
  useEffect(() => {
    if (legacy) navigate(legacy, { replace: true })
  }, [legacy, navigate])

  // biome-ignore lint/correctness/useExhaustiveDependencies: once, on arrival — the notice is a snapshot of the first render
  useEffect(() => {
    if (!signInFailed) return
    const next = new URLSearchParams(searchParams)
    next.delete('connectFailed')
    if (parseView(next.get('view'), viewer.isAdmin) === null) next.delete('view')
    setSearchParams(next, { replace: true })
  }, [])

  const maintained = useKnowledgeBases('maintain')
  const shared = useKnowledgeBases('use')
  const explicit = parseView(searchParams.get('view'), viewer.isAdmin)
  const view: KnowledgeBaseView = explicit ?? defaultView(maintained.data ? maintained.data.items.length : null)
  const everything = useKnowledgeBases('all', viewer.isAdmin && view === 'all')
  const list = view === 'maintain' ? maintained : view === 'use' ? shared : everything
  const allowance = maintained.data?.allowance ?? shared.data?.allowance ?? null

  const term = q.trim()
  const items = (list.data?.items ?? []).filter((base) => matchesQuery(base, term))
  const selected: KnowledgeBase | null =
    [maintained, shared, everything].flatMap((query) => query.data?.items ?? []).find((base) => base.id === baseId) ??
    null

  const viewSuffix = explicit ? `?view=${explicit}` : ''
  const hrefFor = (id: string) => `/knowledge/${encodeURIComponent(id)}${viewSuffix}`
  const setView = (next: KnowledgeBaseView) => {
    const params = new URLSearchParams(searchParams)
    params.set('view', next)
    setSearchParams(params, { replace: true })
  }

  const views: KnowledgeBaseView[] = viewer.isAdmin ? ['maintain', 'use', 'all'] : ['maintain', 'use']
  const countOf = (candidate: KnowledgeBaseView): number | null => {
    const source = candidate === 'maintain' ? maintained : candidate === 'use' ? shared : everything
    return source.data ? source.data.items.length : null
  }

  const deleted = (location.state as DeletedState)?.deleted ?? null
  const showDeleted = deleted !== null && deleted !== dismissed

  const emptyState = () => {
    if (term !== '') return <EmptyState title={t.page.noMatch(term)} body={t.page.noMatchBody} />
    return <EmptyState title={t.page.empty[view]} body={t.page.emptyBody[view]} />
  }

  return (
    <div className="fade-in flex min-h-0 flex-1 flex-col">
      <header className="shrink-0 border-b border-rule px-5 pt-5 sm:px-8">
        <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
          {/* A basis, so on a phone the actions wrap below the lead rather than squeezing it
              into a narrow column beside them. */}
          <div className="min-w-0 flex-1 basis-[16rem]">
            <h1 className="display text-[30px] sm:text-[38px]">{t.page.title}</h1>
            <p className="mt-[7px] max-w-[560px] text-pretty text-[13.5px] leading-[1.5] text-fg-muted">
              {t.page.lead}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="hidden h-[30px] shrink-0 items-center gap-1.5 rounded-full bg-grounded-soft px-3 font-mono text-[9.5px] uppercase tracking-[0.07em] text-grounded sm:flex"
              title={t.page.localHint}
            >
              <CircleCheck size={11} strokeWidth={2.4} aria-hidden />
              {t.page.local}
            </span>
            <Button variant="primary" icon={<Plus size={14} aria-hidden />} onClick={() => setCreating(true)}>
              {t.page.newBase}
            </Button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 pb-4">
          <div className="relative min-w-[13rem] flex-1 basis-[16rem]">
            <label htmlFor={searchId} className="sr-only">
              {t.page.searchLabel}
            </label>
            <Search
              size={14}
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
            />
            <Input
              id={searchId}
              value={q}
              placeholder={t.page.searchPlaceholder}
              className="pl-8"
              onChange={(event) => setQ(event.target.value)}
            />
            {q !== '' && (
              <button
                type="button"
                aria-label={t.page.clearSearch}
                onClick={() => setQ('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-fg-subtle hover:text-fg"
              >
                <X size={13} aria-hidden />
              </button>
            )}
          </div>
          <fieldset className="flex min-w-0 flex-wrap items-center gap-1.5">
            <legend className="sr-only">{t.page.viewsLabel}</legend>
            {views.map((value) => {
              const count = countOf(value)
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={view === value}
                  onClick={() => setView(value)}
                  className={clsx(
                    'inline-flex h-[28px] items-center gap-1.5 rounded-full border px-3 text-[12px] tracking-[-0.01em]',
                    'transition-[color,background-color,border-color] duration-200',
                    view === value
                      ? 'border-rule-strong bg-card-solid font-semibold text-fg'
                      : 'border-rule bg-card text-fg-muted hover:border-rule-strong hover:text-fg'
                  )}
                >
                  {t.page.views[value]}
                  {count !== null && (
                    <>
                      <span aria-hidden className="readout text-fg-subtle">
                        {count}
                      </span>
                      <span className="sr-only">, {t.page.viewCount(fmt, count)}</span>
                    </>
                  )}
                </button>
              )
            })}
          </fieldset>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 lg:divide-x lg:divide-rule">
        <div
          className={clsx(
            'min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-8 lg:max-w-[24rem] lg:px-5',
            baseId && 'hidden lg:block'
          )}
        >
          <h2 id={listLabelId} className="sr-only">
            {t.page.views[view]}
          </h2>

          {showDeleted && (
            <div
              role="status"
              className="mb-3 flex items-start gap-2 rounded-tile border border-rule bg-card-solid/55 px-3 py-2.5"
            >
              <CircleCheck size={13} strokeWidth={2.3} className="mt-[2px] shrink-0 text-grounded" aria-hidden />
              <p className="min-w-0 flex-1 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
                {t.page.deleted(deleted)}
              </p>
              <button
                type="button"
                aria-label={t.page.dismiss}
                onClick={() => setDismissed(deleted)}
                className="rounded-full p-1 text-fg-subtle hover:text-fg"
              >
                <X size={12} aria-hidden />
              </button>
            </div>
          )}

          {signInFailed && (
            <div className="mb-3">
              <Notice title={t.page.signInFailed} icon={<TriangleAlert size={13} aria-hidden />} role="alert">
                {t.page.signInFailedBody}
                <span className="readout mt-1 block text-fg-subtle">{signInFailed}</span>
              </Notice>
            </div>
          )}

          {list.isLoading && (
            <p className="flex items-center gap-2 text-[13px] text-fg-subtle">
              <Spinner size={12} /> {t.page.loading}
            </p>
          )}
          {list.isError && <EmptyState title={t.page.failed} body={t.page.failedBody} />}
          {!list.isLoading && !list.isError && items.length === 0 && emptyState()}

          <ul aria-labelledby={listLabelId} className="space-y-2">
            {items.map((base, index) => (
              <li key={base.id}>
                <BaseRow base={base} to={hrefFor(base.id)} selected={base.id === baseId} index={index} />
              </li>
            ))}
          </ul>
        </div>

        <div className={clsx('min-h-0 flex-1 overflow-y-auto', !baseId && 'hidden lg:block')}>
          {baseId ? (
            <BasePanel
              baseId={baseId}
              fallback={selected}
              allowance={allowance}
              uploadMaxBytes={allowance?.uploadMaxBytes ?? null}
              onBack={() => navigate(`/knowledge${viewSuffix}`)}
              onDeleted={(name) => navigate(`/knowledge${viewSuffix}`, { replace: true, state: { deleted: name } })}
            />
          ) : (
            <div className="flex h-full items-center justify-center px-6">
              <EmptyState title={t.page.pickOne} body={t.page.pickOneBody} />
            </div>
          )}
        </div>
      </div>

      {creating && (
        <NewBaseDialog
          allowance={allowance}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false)
            navigate(hrefFor(id))
          }}
        />
      )}
    </div>
  )
}
